import { cn } from '@mdvip/shared/lib/utils';

/**
 * Semantic icon name → Font Awesome 7 Pro glyph.
 *
 * Mirrors the design system's `FA_GLYPH` map (Payment Center `runtime.jsx`) so our names match
 * the design's, plus the glyphs the two portal app-shells need that the Payment Center prototype
 * never exercised (grouped below). Add a name here when a feature needs a new glyph — the full FA
 * Pro glyph map is already shipped in `styles/fontawesome-pro.css`, so any FA name resolves.
 */
const FA_GLYPH = {
  // Design system map (Payment Center)
  layoutGrid: 'grid-2',
  messageSquare: 'message',
  bookOpen: 'book-open',
  creditCard: 'credit-card',
  search: 'magnifying-glass',
  bell: 'bell',
  chevronDown: 'chevron-down',
  chevronRight: 'chevron-right',
  chevronLeft: 'chevron-left',
  check: 'check',
  circleCheck: 'circle-check',
  x: 'xmark',
  plus: 'plus-large',
  minus: 'minus',
  pencil: 'pencil',
  trash: 'trash-can',
  info: 'circle-info',
  fileText: 'file-lines',
  mail: 'envelope',
  calendar: 'calendar',
  clock: 'clock',
  alertTriangle: 'triangle-exclamation',
  alertCircle: 'circle-exclamation',
  shieldCheck: 'shield-check',
  landmark: 'landmark',
  wallet: 'wallet',
  refreshCw: 'arrows-rotate',
  download: 'download',
  sun: 'sun',
  moon: 'moon',
  sliders: 'sliders',
  dollarSign: 'dollar-sign',
  stethoscope: 'stethoscope',
  plane: 'plane',
  utensils: 'utensils',
  dumbbell: 'dumbbell',
  lock: 'lock',
  arrowRight: 'arrow-right',
  phone: 'phone',
  command: 'command',
  menu: 'bars',
  user: 'user',
  // App-shell additions (CNP-228) — nav, offline indicator, external links
  building: 'building',
  chevronUp: 'chevron-up',
  externalLink: 'arrow-up-right-from-square',
  logOut: 'right-from-bracket',
  textSize: 'font',
  userCog: 'user-gear',
  users: 'users',
  wifi: 'wifi',
  wifiOff: 'wifi-slash',
} as const;

export type IconName = keyof typeof FA_GLYPH;

/** Every supported icon name (handy for galleries, pickers, and enumeration). */
export const ICON_NAMES = Object.keys(FA_GLYPH) as IconName[];

/**
 * FA Pro weight (face). Default is `regular`; affirmative/emphasis marks typically use `solid`.
 * Only the three self-hosted faces are exposed — we don't ship the `thin` (100) woff2.
 */
export type IconWeight = 'light' | 'regular' | 'solid';

/** Icon-font glyphs scale with `font-size`, so size maps to a font-size utility (not width/height). */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<IconSize, string> = {
  xs: 'text-xs', // 12px
  sm: 'text-sm', // 14px
  md: 'text-base', // 16px
  lg: 'text-lg', // 18px
  xl: 'text-xl', // 20px
};

export interface IconProps {
  /** Semantic icon name (mapped to a Font Awesome 7 Pro glyph). */
  name: IconName;
  /** FA Pro face. Defaults to `regular`. */
  weight?: IconWeight;
  /** Glyph size as a font-size token. Defaults to `md` (16px). Override freely via `className`. */
  size?: IconSize;
  /** Extra classes — typically a color token (`text-muted-foreground`) or a size override. */
  className?: string;
  /**
   * Accessible label. When provided the icon is exposed to assistive tech (`role="img"`);
   * otherwise it is treated as decorative (`aria-hidden`) — the default, since most icons sit
   * beside a visible text label.
   */
  label?: string;
}

/**
 * Renders a Font Awesome 7 Pro glyph as an `<i>`. The glyph inherits `currentColor`, so `text-*`
 * color utilities tint it, and it scales with the `size` token (font-size). Built on the self-hosted
 * FA Pro webfonts + glyph CSS in `styles/fontawesome-pro.css`.
 */
export function Icon({ name, weight = 'regular', size = 'md', className, label }: IconProps) {
  const a11y = label ? ({ role: 'img', 'aria-label': label } as const) : ({ 'aria-hidden': true } as const);
  return (
    <i
      className={cn(
        `fa-${weight}`,
        `fa-${FA_GLYPH[name]}`,
        SIZE_CLASS[size],
        // FA's own `.fa { width: 1.25em }` is unlayered and beats Tailwind's width, so pin a square
        // glyph box via FA's `--fa-width` knob (matches lucide/the design's square icon footprint).
        'pointer-events-none inline-flex size-[1em] shrink-0 items-center justify-center align-middle leading-none [--fa-width:1em]',
        className,
      )}
      {...a11y}
    />
  );
}
